import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';

import Gateway from '../Gateway';
import { useTheme } from '../../../../theme';
import { PaymentPage, PaymentType } from '../../../types';
import Button from '../../../../Button';
import { InvoiceMethodFragment, CompletePaymentMutationVariables } from '@websky/graphql';
import Input from '../../../../Input';
import { Money } from '../../../../Money';

interface Props {
	isPreOpened?: boolean;
	frameInfo: InvoiceMethodFragment;
	headerTitle?: string;
	type: PaymentType;
	onCompletePayment: (values: Omit<CompletePaymentMutationVariables, 'orderId'>) => Promise<boolean>;
	onClick: (page: PaymentPage) => void;
}

const Invoice: React.FC<Props> = ({
	children,
	isPreOpened,
	frameInfo,
	headerTitle,
	type,
	onCompletePayment,
	onClick
}) => {
	const { Invoice: css } = useTheme('PaymentForm');
	const { t } = useTranslation('PaymentForm');

	const onCompletePaymentHandler = useCallback(
		(values: Partial<CompletePaymentMutationVariables>) =>
			onCompletePayment({ transactionId: frameInfo.transactionId, ...values }),
		[onCompletePayment, frameInfo]
	);

	const onClickGateway = useCallback(
		() =>
			onClick({
				title: headerTitle,
				type: PaymentType.Invoice,
				content: (
					<Form
						onSubmit={onCompletePaymentHandler}
						render={({ handleSubmit }) => (
							<div className={css.content}>
								<form onSubmit={handleSubmit}>
									<Field
										name="additionalText"
										render={({ input }) => (
											<Input
												{...input}
												inputProps={{ 'data-test-id': 'invoice-additional-text' }}
												TextFieldProps={{
													label: t('Payment order numbers'),
													onChange: input.onChange
												}}
											/>
										)}
									/>

									<div className={css.footer}>
										<Button type="submit">
											{t('Pay')}
											<Money money={frameInfo.totalPrice} />
										</Button>
									</div>
								</form>
							</div>
						)}
					/>
				)
			}),
		[onCompletePayment, onClick, frameInfo]
	);

	useEffect(() => {
		if (isPreOpened) {
			onClickGateway();
		}
	}, [isPreOpened]);

	return <Gateway header={children} onClick={onClickGateway} type={type} paymentType={PaymentType.Invoice} />;
};

export default Invoice;
